/**
 * Shared layout primitives for public auth pages (Login, ForgotPassword, AcceptInvite).
 *
 * Centralises the three elements duplicated across all first-touch pages:
 *   - filledInputSx  — filled-variant TextField overrides
 *   - outerBoxSx     — full-viewport centred column container
 *   - BrandMark      — the LeadCraft mark (BOLT-404)
 */
import { Box, Typography } from '@mui/material';
import { BRAND } from '@crm98/shared';
import leadcraftIcon from '../assets/leadcraft-icon.png';

export const filledInputSx = {
  '& .MuiFilledInput-root': {
    borderRadius: 1.5,
    bgcolor: '#e4e6ef',
    '&:hover': { bgcolor: '#dcdff0' },
    '&.Mui-focused': { bgcolor: '#dcdff0' },
    '&:before': { display: 'none' },
    '&:after': { display: 'none' },
  },
  '& .MuiFilledInput-input': { paddingTop: '14px', paddingBottom: '14px' },
  '& .MuiFilledInput-root .MuiInputAdornment-root': { color: 'text.disabled' },
};

export const outerBoxSx = {
  minHeight: '100vh',
  bgcolor: '#eff1f8',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  px: 2,
} as const;

/**
 * The login-screen brand mark: the supplied LeadCraft icon plus a two-tone
 * text wordmark (first half in the text colour, second in brand blue). The
 * wordmark is real text rather than the supplied lockup image because that
 * image is a 666×375 raster with the blur baked in — text stays sharp at every
 * size. Both halves come from BRAND.productNameParts, so this screen cannot be
 * the one place a rename misses. The old tagline ("#More Than Just A Website")
 * is retired with the 97 Display brand (user ruling 2026-09-09), not carried
 * over.
 */
export function BrandMark() {
  const [wordmarkHead, wordmarkTail] = BRAND.productNameParts;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
      <Box
        component="img"
        src={leadcraftIcon}
        alt=""
        sx={{ height: 56, width: 'auto', flexShrink: 0 }}
      />
      <Typography component="span" sx={{ fontWeight: 800, fontSize: '2.25rem', lineHeight: 1 }}>
        <Box component="span" sx={{ color: 'text.primary' }}>
          {wordmarkHead}
        </Box>
        <Box component="span" sx={{ color: 'primary.main' }}>
          {wordmarkTail}
        </Box>
      </Typography>
    </Box>
  );
}
